import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminBatchStudents() {
  const { batchid } = useParams();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removingId, setRemovingId] = useState(null);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in to view students.');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${apiUrl}/api/admin/batch-students/${batchid}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setStudents(response.data.students);
      } else {
        setError(response.data.message || 'Failed to fetch students.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching students.');
    } finally {
      setLoading(false);
    }
  }, [batchid]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleRemove = async (student) => {
    const name = `${student.firstName} ${student.lastName}`.trim();
    if (!window.confirm(`Remove ${name} from this batch? This only removes them from this batch — their profile and other enrollments are unaffected.`)) return;
    setRemovingId(student.allocationId);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) return;
      const response = await axios.delete(`${apiUrl}/api/admin/student-allocations/${student.allocationId}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setStudents((prev) => prev.filter((s) => s.allocationId !== student.allocationId));
      } else {
        setError(response.data.message || 'Failed to remove student from batch.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error removing student from batch.');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <Link to="/admin-view-batches" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    View Batches
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Batch Students</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="alert alert-info mb-16" role="alert">
            To move a student to a different batch: remove them from this batch below, then go to{' '}
            <Link to="/admin-student-allocation" className="fw-semibold">Student Course Allocations</Link> and use "Add Course" to assign their new course and batch.
          </div>
          <div className="card overflow-hidden">
            <div className="card-body p-0 overflow-x-auto">
              {loading && (
                <div className="alert alert-info mb-0" role="alert">
                  Loading students...
                </div>
              )}
              {error && (
                <div className="alert alert-danger mb-0" role="alert">
                  {error}
                </div>
              )}
              {!loading && !error && students.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No students found in this batch.
                </div>
              )}
              {!loading && !error && students.length > 0 && (
                <table id="studentTable" className="table table-striped">
                  <thead>
                    <tr>
                      <th className="fixed-width">
                        <div className="form-check">
                          <input
                            className="form-check-input border-gray-200 rounded-4"
                            type="checkbox"
                            id="selectAll"
                          />
                        </div>
                      </th>
                      <th className="h6 text-gray-300">First Name</th>
                      <th className="h6 text-gray-300">Last Name</th>
                      <th className="h6 text-gray-300">Email</th>
                      <th className="h6 text-gray-300">Mobile</th>
                      <th className="h6 text-gray-300">Course</th>
                      <th className="h6 text-gray-300">Videos Visible From</th>
                      <th className="h6 text-gray-300">Lessons Visible</th>
                      <th className="h6 text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student.allocationId}>
                        <td className="fixed-width">
                          <div className="form-check">
                            <input
                              className="form-check-input border-gray-200 rounded-4"
                              type="checkbox"
                            />
                          </div>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {student.firstName}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {student.lastName}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {student.email}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {student.mobile}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {student.allocationCourseName || 'Unknown'}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {Number(student.batchRollingEnrollment) === 1
                              ? (student.allocationDateAssigned ? new Date(student.allocationDateAssigned).toLocaleDateString() : 'Unknown')
                              : 'All lessons'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`h6 mb-0 fw-medium ${Number(student.visibleLessonsCount) === 0 ? 'text-danger-600' : 'text-gray-300'}`}
                            title={Number(student.batchRollingEnrollment) === 1 ? 'Lessons dated on/after this student\'s join date' : 'All lessons in this batch'}
                          >
                            {student.visibleLessonsCount}
                          </span>
                        </td>
                        <td>
                          <Link
                            to={`/admin-edit-student/${student.id}`}
                            className="bg-main-50 text-main-600 py-2 px-14 rounded-pill hover-bg-main-600 hover-text-white"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleRemove(student)}
                            disabled={removingId === student.allocationId}
                            style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: removingId === student.allocationId ? 0.6 : 1 }}
                            className="py-2 px-14 rounded-pill ms-8 border-0"
                          >
                            {removingId === student.allocationId ? 'Removing...' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="card-footer flex-between flex-wrap">
              <span className="text-gray-900">
                Showing {students.length} of {students.length} entries
              </span>
              <ul className="pagination flex-align flex-wrap">
                <li className="page-item active">
                  <button
                    className="page-link h-44 w-44 flex-center text-15 rounded-8 fw-medium"
                  >
                    1
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminBatchStudents;